// src/app/services/Clarity.service.ts
import { Injectable, Inject, PLATFORM_ID, OnDestroy } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { BehaviorSubject, Subject, filter, takeUntil } from 'rxjs';


// Import du package officiel Microsoft Clarity
import Clarity from '@microsoft/clarity';

// Import des types depuis le fichier dédié
import {
    ClarityConfig,
    ClarityUser,
    ClarityEventData,
    ClarityEventType
} from './clarity.types';
import { environment } from '../../../../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class ClarityService implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    private readonly isInitialized$ = new BehaviorSubject<boolean>(false);
    private config: ClarityConfig;
    private currentUser: ClarityUser | null = null;
    isActive = false;
    version = '2';

    constructor(
        @Inject(PLATFORM_ID) private platformId: Object,
        private router: Router
    ) {
        this.config = environment?.clarity;

       this.isActive = this.shouldInitialize()
    }

   async init(){
        if (this.isActive) {
           await this.initializeClarity();
            this.setupRouterTracking();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ============================================================
    // MÉTHODES PRIVÉES
    // ============================================================

    /**
     * Vérifie si Clarity doit être initialisé
     */
    private shouldInitialize(): boolean {
        const isBrowser = isPlatformBrowser(this.platformId);
        const isEnabled = this.config?.enabled && !!this.config?.projectId;
        const isDevelopment = !environment.production;
        const allowInDev = this.config?.enableInDevelopment;

        return isBrowser && isEnabled && (!isDevelopment || allowInDev);
    }

    /**
     * Initialise Microsoft Clarity
     */
    private async initializeClarity(): Promise<void> {
        try {
            if (!this.config.projectId) {
                console.warn('[Clarity] Project ID is missing. Initialization aborted.');

                return
            }

            // Initialisation directe avec l'import npm
            Clarity.init(this.config.projectId);

            this.isInitialized$.next(true);

            console.log(`[Clarity] Initialized with Project ID: ${this.config.projectId}`);

            // Gérer le consentement RGPD si configuré
            if (this.config.cookieConsent) {
                this.handleCookieConsent();
            }

        } catch (error) {
            console.error('[Clarity] Initialization failed:', error);
            this.isInitialized$.next(false);
        }
    }

    /**
     * Configure le suivi automatique des pages via le Router
     */
    private setupRouterTracking(): void {
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe((event: NavigationEnd) => {
                this.trackPageView(event.urlAfterRedirects);
            });
    }

    /**
     * Gère le consentement des cookies RGPD
     */
    private handleCookieConsent(): void {
        const consent = localStorage.getItem('clarity-consent');
        if (consent === 'true') {
            this.consent(true);
        } else if (consent === 'false') {
            this.consent(false);
        }
    }

    // ============================================================
    // MÉTHODES PUBLIQUES - API DE BASE
    // ============================================================

    /**
     * Vérifie si Clarity est initialisé
     */
    public isInitialized(): boolean {
        return this.isInitialized$.value;
    }

    /**
     * Observable pour savoir si Clarity est initialisé
     */
    public get initialized$() {
        return this.isInitialized$.asObservable();
    }

    /**
     * Donne ou retire le consentement pour le suivi (RGPD)
     */
    public consent(hasConsent: boolean = true): void {
        if (!this.isInitialized()) return;

        try {
            Clarity.consent(hasConsent);

            localStorage.setItem('clarity-consent', String(hasConsent));
            console.log(`[Clarity] Consent ${hasConsent ? 'granted' : 'revoked'}`);
        } catch (error) {
            console.error('[Clarity] Failed to set consent:', error);
        }
    }

    /**
     * Retire le consentement
     */
    public revokeConsent(): void {
        this.consent(false);
    }

    /**
     * Identifie un utilisateur
     */
    public identify(user: ClarityUser): void {
        if (!this.isInitialized()) return;

        try {
            this.currentUser = user;

            Clarity.identify(user.id, user.sessionId, user.pageId, user.friendlyName);

            // Définir des propriétés utilisateur personnalisées
            this.set('user_id', user.id);
            if (user.sessionId) {
                this.set('session_id', user.sessionId);
            }

            console.log(`[Clarity] User identified: ${user.id}`);
        } catch (error) {
            console.error('[Clarity] Failed to identify user:', error);
        }
    }

    /**
     * Obtient l'utilisateur courant
     */
    public getCurrentUser(): ClarityUser | null {
        return this.currentUser;
    }

    /**
     * Déconnecte l'utilisateur actuel
     */
    public clearUser(): void {
        this.currentUser = null;
        this.event(ClarityEventType.USER_LOGOUT);
        console.log('[Clarity] User cleared');
    }

    /**
     * Définit une variable personnalisée
     */
    public set(key: string, value: string | number | boolean | string[]): void {
        if (!this.isInitialized()) return;

        try {
            Clarity.setTag(key, Array.isArray(value) ? value : String(value));
            console.log(`[Clarity] Set ${key}:`, value);
        } catch (error) {
            console.error(`[Clarity] Failed to set ${key}:`, error);
        }
    }

    /**
     * Définit plusieurs variables personnalisées
     */
    public setMultiple(data: ClarityEventData): void {
        Object.entries(data).forEach(([key, value]) => {
            this.set(key, value);
        });
    }

    /**
     * Envoie un événement personnalisé
     */
    public event(name: string | ClarityEventType, data?: ClarityEventData): void {
        if (!this.isInitialized()) return;

        try {
            const eventName = String(name);

            Clarity.event(eventName);

            // Ajouter des données contextuelles si fournies
            if (data) {
                Object.entries(data).forEach(([key, value]) => {
                    this.set(`event_${key}`, value);
                });
            }

            console.log(`[Clarity] Event tracked: ${eventName}`, data);
        } catch (error) {
            console.error(`[Clarity] Failed to track event ${name}:`, error);
        }
    }

    /**
     * Améliore la session (upgrade)
     */
    public upgrade(reason: string): void {
        if (!this.isInitialized()) return;

        try {
            Clarity.upgrade(reason);
            console.log(`[Clarity] Session upgraded: ${reason}`);
        } catch (error) {
            console.error('[Clarity] Failed to upgrade session:', error);
        }
    }

    // ============================================================
    // MÉTHODES DE TRACKING SPÉCIALISÉES
    // ============================================================

    /**
     * Suit une page vue
     */
    public trackPageView(url?: string): void {
        const currentUrl = url || this.router.url;

        this.event(ClarityEventType.PAGE_VIEW);

        // Mettre à jour des variables avec les infos de la page
        this.set('current_page', currentUrl);
        this.set('page_timestamp', new Date().toISOString());
    }

    /**
     * Suit un clic sur un élément
     */
    public trackClick(element: string, additionalData?: ClarityEventData): void {
        this.event(ClarityEventType.CLICK);

        if (additionalData) {
            this.setMultiple({
                click_element: element,
                click_timestamp: Date.now(),
                ...additionalData
            });
        }
    }

    /**
     * Suit une soumission de formulaire
     */
    public trackFormSubmit(formName: string, additionalData?: ClarityEventData): void {
        this.event(ClarityEventType.FORM_SUBMIT);

        this.setMultiple({
            form_name: formName,
            form_timestamp: Date.now(),
            ...additionalData
        });
    }

    /**
     * Suit une recherche
     */
    public trackSearch(query: string, results?: number): void {
        this.event(ClarityEventType.SEARCH);

        this.setMultiple({
            search_query: query,
            search_results: results || 0,
            search_timestamp: Date.now()
        });
    }

    /**
     * Suit un achat
     */
    public trackPurchase(orderId: string, amount: number, currency: string = 'EUR'): void {
        this.event(ClarityEventType.PURCHASE);

        this.setMultiple({
            order_id: orderId,
            purchase_amount: amount,
            purchase_currency: currency,
            purchase_timestamp: Date.now()
        });
    }

    /**
     * Suit une erreur
     */
    public trackError(error: Error | string, context?: string): void {
        const errorMessage = error instanceof Error ? error.message : error;
        const errorStack = error instanceof Error ? error.stack : '';

        this.event(ClarityEventType.ERROR);

        this.setMultiple({
            error_message: errorMessage,
            error_stack: errorStack || '',
            error_context: context || 'unknown',
            error_timestamp: Date.now()
        });
    }
    public trackErrorByType(clarityEventType:ClarityEventType, error: Error | any, context?: string): void {
        const errorMessage = error instanceof Error ? error.message : error;
        const errorStack = error instanceof Error ? error.stack : '';

        this.event(clarityEventType+'-'+this.version);

        this.setMultiple({
            error_message:JSON.stringify(errorMessage) ,
            error_stack: errorStack || '',
            error_context: context || 'unknown',
            error_timestamp: Date.now()
        });
    }

    // ============================================================
    // MÉTHODES E-COMMERCE
    // ============================================================

    /**
     * Suit la vue d'un produit
     */
    public trackProductView(productId: string, category?: string, price?: number): void {
        this.event('product_view');
        this.setMultiple({
            product_id: productId,
            product_category: category || 'unknown',
            product_price: price || 0
        });
    }

    /**
     * Suit l'ajout au panier
     */
    public trackAddToCart(productId: string, quantity: number = 1): void {
        this.event('add_to_cart');
        this.setMultiple({
            product_id: productId,
            cart_quantity: quantity
        });
    }

    /**
     * Suit les étapes de checkout
     */
    public trackCheckoutStep(step: number, option?: string): void {
        this.event('checkout_progress');
        this.setMultiple({
            checkout_step: step,
            checkout_option: option || ''
        });
    }

    // ============================================================
    // MÉTHODES SAAS
    // ============================================================

    /**
     * Suit l'utilisation d'une fonctionnalité
     */
    public trackFeatureUsage(featureName: string): void {
        this.event('feature_used');
        this.set('feature_name', featureName);
    }

    /**
     * Suit une conversion d'essai
     */
    public trackTrialConversion(planName: string): void {
        this.event('trial_conversion');
        this.set('plan_name', planName);
        this.upgrade('trial_conversion'); // Session importante
    }

    /**
     * Suit la connexion utilisateur
     */
    public trackUserLogin(userId: string, method: string = 'email'): void {
        this.event(ClarityEventType.USER_LOGIN);
        this.setMultiple({
            login_user_id: userId,
            login_method: method
        });
    }

    // ============================================================
    // MÉTHODES UTILITAIRES
    // ============================================================

    /**
     * Obtient la configuration actuelle
     */
    public getConfig(): ClarityConfig {
        return { ...this.config };
    }

    /**
     * Met à jour la configuration
     */
    public updateConfig(newConfig: Partial<ClarityConfig>): void {
        this.config = { ...this.config, ...newConfig };
        console.log('[Clarity] Configuration updated:', this.config);
    }

    /**
     * Obtient des informations sur la session
     */
    public getSessionInfo(): any {
        if (!this.isInitialized()) return null;

        return {
            isInitialized: this.isInitialized(),
            currentUser: this.currentUser,
            config: this.getConfig(),
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString()
        };
    }

    /**
     * Active le mode debug
     */
    public enableDebug(): void {
        if (!this.isInitialized()) return;

        this.set('debug_mode', true);
        console.log('[Clarity] Debug mode enabled');
    }

    /**
     * Désactive le mode debug
     */
    public disableDebug(): void {
        if (!this.isInitialized()) return;

        this.set('debug_mode', false);
        console.log('[Clarity] Debug mode disabled');
    }

    /**
     * Définit des métadonnées utilisateur
     */
    public setUserMetadata(metadata: ClarityEventData): void {
        const prefixedData: ClarityEventData = {};
        Object.entries(metadata).forEach(([key, value]) => {
            prefixedData[`user_${key}`] = value;
        });
        this.setMultiple(prefixedData);
    }

    /**
     * Définit des métadonnées de page
     */
    public setPageMetadata(metadata: ClarityEventData): void {
        const prefixedData: ClarityEventData = {};
        Object.entries(metadata).forEach(([key, value]) => {
            prefixedData[`page_${key}`] = value;
        });
        this.setMultiple(prefixedData);
    }

    /**
     * Suit une interaction personnalisée
     */
    public trackInteraction(type: string, target: string, data?: ClarityEventData): void {
        this.event(`interaction_${type}`);
        this.setMultiple({
            interaction_type: type,
            interaction_target: target,
            interaction_timestamp: Date.now(),
            ...data
        });
    }

    /**
     * Définit des variables A/B testing
     */
    public setABTestVariant(testName: string, variant: string): void {
        this.set(`ab_test_${testName}`, variant);
        this.event('ab_test_assigned');
    }

    /**
     * Suit les métriques de performance
     */
    public trackPerformanceMetric(metricName: string, value: number, unit?: string): void {
        this.event('performance_metric');
        this.setMultiple({
            metric_name: metricName,
            metric_value: value,
            metric_unit: unit || 'ms'
        });
    }

    /**
     * Nettoie les données utilisateur (RGPD)
     */
    public clearUserData(): void {
        this.currentUser = null;
        localStorage.removeItem('clarity-consent');
        console.log('[Clarity] User data cleared');
    }

    /**
     * Vérifie si le consentement a été donné
     */
    public hasConsent(): boolean {
        return localStorage.getItem('clarity-consent') === 'true';
    }

    /**
     * Obtient des statistiques de session
     */
    public getSessionStats(): any {
        return {
            initialized: this.isInitialized(),
            hasUser: !!this.currentUser,
            hasConsent: this.hasConsent(),
            config: this.config,
            startTime: new Date().toISOString()
        };
    }
}
